import { Building2, Phone, MapPin, Hash, Calendar, ShieldCheck, Mail, Award, Clock, Globe, Users, Briefcase, Edit, CheckCircle, Store } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Card, Avatar, Skeleton } from "../components/ui";
import { StatusChip } from "../components/Chips";

export default function Profile() {
  const { partner, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-5xl mx-auto">
          <Skeleton className="h-7 sm:h-8 w-32 sm:w-48 mb-4 sm:mb-6" />
          <Card className="p-4 sm:p-6 md:p-8 bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
              <Skeleton className="h-16 w-16 sm:h-20 sm:w-20 rounded-full" />
              <div className="flex-1 space-y-2 sm:space-y-3 w-full sm:w-auto">
                <Skeleton className="h-6 sm:h-8 w-32 sm:w-48 mx-auto sm:mx-0" />
                <Skeleton className="h-4 sm:h-5 w-40 sm:w-64 mx-auto sm:mx-0" />
                <Skeleton className="h-5 sm:h-6 w-20 sm:w-24 mx-auto sm:mx-0" />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
        <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-4xl mx-auto">
          <Card className="p-8 sm:p-12 text-center bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-emerald-100 rounded-full">
                <Users className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">No Profile Data</h2>
              <p className="text-sm text-gray-500">Unable to load your profile information. Please try again later.</p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const profileData = partner;
  
  const partnerName = profileData.name || 'Partner';
  const businessName = profileData.registeredBusinessName || profileData.businessName || '—';
  const partnerId = profileData.partnerId || '—';
  const phone = profileData.phone || '—';
  const username = profileData.username || '—';
  const businessType = profileData.businessType || '—';
  const gstNumber = profileData.gstNumber || '—';
  const status = profileData.status || 'ACTIVE';
  // CHAIN | BRANCH — the tier this login belongs to (docs §6.1).
  const kind = profileData.kind;
  const createdAt = profileData.createdAt || null;
  const lastLoginAt = profileData.lastLoginAt || null;
  
  const location = profileData.location || {};
  const address = location.address || '—';
  const city = location.city || '—';
  const state = location.state || '—';
  const pincode = location.pincode || '—';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-5xl mx-auto">
        <PageHeader 
          title="Profile" 
          subtitle="Your organization's details as registered with Alfennzo." 
        />

        {/* Profile Header Card */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 to-green-700 shadow-2xl shadow-emerald-500/25">
          {/* Decorative elements - lighter and cleaner */}
          <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-32 sm:w-48 h-32 sm:h-48 bg-black/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" />
          <div className="absolute top-1/2 left-1/2 w-40 sm:w-56 h-40 sm:h-56 bg-white/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          
          <div className="relative px-4 sm:px-6 md:px-8 py-6 sm:py-8 flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6">
            <div className="relative flex-shrink-0">
              <Avatar 
                name={partnerName} 
                size={72} 
                className="ring-4 ring-white/40 shadow-2xl shadow-black/20 text-2xl sm:text-3xl font-bold bg-white/20 backdrop-blur-sm border-2 border-white/50"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
                <CheckCircle size={10} className="text-white" />
              </div>
            </div>
            
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-white">{partnerName}</h1>
                <StatusChip status={status} className="bg-white/20 text-white border-white/30 backdrop-blur-sm text-xs" />
              </div>
              <p className="text-emerald-100/90 text-base sm:text-lg font-medium flex items-center justify-center md:justify-start gap-2">
                <Building2 size={16} className="hidden sm:block" />
                {businessName}
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 sm:gap-3 mt-2">
                {/* `kind` is what the whole panel keys off — a chain manages
                    branches, a branch manages staff — so it belongs on the
                    profile rather than being inferred from the visible menu. */}
                <span className="flex items-center gap-1.5 text-emerald-50 text-xs sm:text-sm font-semibold bg-white/20 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full backdrop-blur-sm border border-white/25">
                  {kind === "CHAIN" ? <Building2 size={12} /> : <Store size={12} />}
                  {kind === "CHAIN" ? "Chain" : "Branch"}
                </span>
                <span className="flex items-center gap-1.5 text-emerald-100/80 text-xs sm:text-sm bg-white/10 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full backdrop-blur-sm">
                  <Hash size={12} />
                  {partnerId}
                </span>
                <span className="flex items-center gap-1.5 text-emerald-100/80 text-xs sm:text-sm bg-white/10 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full backdrop-blur-sm">
                  <Calendar size={12} />
                  Since {createdAt ? new Date(createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                </span>
                {lastLoginAt && (
                  <span className="flex items-center gap-1.5 text-emerald-100/80 text-xs sm:text-sm bg-white/10 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full backdrop-blur-sm">
                    <Clock size={12} />
                    Last login {new Date(lastLoginAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            {/* Status Badge */}
            <div className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-white/20 min-w-[100px] text-center flex-shrink-0">
              <p className="text-emerald-100/70 text-[10px] font-medium uppercase tracking-wider">Status</p>
              <p className="text-white font-bold text-base mt-0.5 flex items-center justify-center gap-1.5">
                <span className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />
                {status}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Row - Cleaner Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 my-4 sm:my-6">
          {[
            { icon: Users, label: 'Business Type', value: businessType, color: 'emerald' },
            { icon: Phone, label: 'Phone', value: `+91 ${phone}`, color: 'blue' },
            { icon: ShieldCheck, label: 'GST Number', value: gstNumber, color: 'purple' },
            { icon: Globe, label: 'Location', value: city || '—', color: 'indigo' }
          ].map((item, index) => (
            <div key={index} className="bg-white/90 backdrop-blur-sm rounded-xl border border-gray-100/80 p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-200">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-1.5 sm:p-2 rounded-lg bg-${item.color}-50`}>
                  <item.icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-${item.color}-600`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] sm:text-xs text-gray-400 font-medium">{item.label}</p>
                  <p className="text-xs sm:text-sm font-semibold text-gray-700 truncate">{item.value || '—'}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Information Grid */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
          {/* Location Details */}
          <Card className="p-4 sm:p-6 bg-white/90 backdrop-blur-sm border border-gray-100/80 shadow-sm hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Location Details</h3>
                <p className="text-[10px] sm:text-xs text-gray-400">Registered business address</p>
              </div>
            </div>
            {location && Object.keys(location).length > 0 && address !== '—' ? (
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start gap-3 p-3 bg-gray-50/80 rounded-xl border border-gray-100">
                  <MapPin className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-700 font-medium">{address}</p>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {city}, {state} - {pincode}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    City: <span className="text-gray-700 font-medium">{city}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    State: <span className="text-gray-700 font-medium">{state}</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                    PIN: <span className="text-gray-700 font-mono font-medium">{pincode}</span>
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No location details available</p>
            )}
          </Card>

          {/* Account Information */}
          <Card className="p-4 sm:p-6 bg-white/90 backdrop-blur-sm border border-gray-100/80 shadow-sm hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="p-2 bg-emerald-100 rounded-xl">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Account Information</h3>
                <p className="text-[10px] sm:text-xs text-gray-400">Security & access details</p>
              </div>
            </div>
            <div className="space-y-3 sm:space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50/80 rounded-xl border border-gray-100 gap-2 sm:gap-0">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-400">Username</p>
                    <p className="text-xs sm:text-sm font-semibold text-gray-700 font-mono">{username}</p>
                  </div>
                </div>
                <StatusChip status="VERIFIED" className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] sm:text-xs px-2 py-0.5" />
              </div>
              
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50/80 rounded-xl border border-gray-100 gap-2 sm:gap-0">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 bg-emerald-100 rounded-lg">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-[10px] sm:text-xs text-gray-400">Partner Since</p>
                    <p className="text-xs sm:text-sm font-semibold text-gray-700">
                      {createdAt ? new Date(createdAt).toLocaleDateString('en-US', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric' 
                      }) : '—'}
                    </p>
                  </div>
                </div>
                {lastLoginAt && (
                  <div className="text-left sm:text-right">
                    <p className="text-[10px] sm:text-xs text-gray-400">Last Login</p>
                    <p className="text-[10px] sm:text-xs font-medium text-gray-600">
                      {new Date(lastLoginAt).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 p-2.5 bg-gray-50/80 rounded-xl border border-gray-100">
                <Clock className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-[10px] sm:text-xs text-gray-500">
                  Account created {createdAt ? new Date(createdAt).toLocaleDateString('en-US', { 
                    day: 'numeric', 
                    month: 'short', 
                    year: 'numeric' 
                  }) : '—'}
                </span>
              </div>
            </div>
          </Card>
        </div>

        {/* Trust & Security Badge - Cleaner */}
        <div className="mt-4 sm:mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-6 p-3 sm:p-4 bg-white/90 backdrop-blur-sm rounded-xl border border-gray-100/80 shadow-sm">
          {[
            { icon: ShieldCheck, label: '256-bit SSL Encrypted' },
            { icon: Award, label: 'Verified Partner' },
            { icon: Clock, label: '24/7 Support' },
            { icon: Globe, label: 'GDPR Compliant' }
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600">
              <item.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />
              <span className="hidden xs:inline">{item.label}</span>
              <span className="xs:hidden">{item.label.split(' ')[0]}</span>
              {index < 3 && <span className="w-px h-4 bg-gray-200 ml-1 sm:ml-2 hidden xs:block" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}